'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCallback, useState } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import httpClient from '../../../http/http-client'
import { publicBucketBaseUrl } from '../../../constants/public-bucket'
import { Button } from 'ui/components/button'
import { Input } from 'ui/components/input'
import { Card, CardContent, CardHeader } from 'ui/components/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'ui/components/form'
import KachLogo from 'kach-components/src/components/KachLogo'
import Image from 'next/image'

const LoginSchema = z.object({
  user: z.string().min(1, 'Campo requerido'),
  password: z.string().min(1, 'Campo requerido'),
})

type ILoginSchema = z.infer<typeof LoginSchema>

export default function LoginPage() {
  const form = useForm<ILoginSchema>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      user: '',
      password: '',
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const onLogin = useCallback(
    async (input: ILoginSchema) => {
      setIsLoading(true)
      const toastId = toast.loading('Verificando credenciales...')

      try {
        await httpClient.post('/v1/backoffice/login', input)
        toast.success('Credenciales validadas correctamente', { id: toastId })
        router.push('/companies')
        router.refresh()
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message || 'Error al iniciar sesión'
        toast.error(errorMessage, { id: toastId })
        setIsLoading(false)
      }
    },
    [router],
  )

  return (
    <div
      className='flex min-h-screen flex-col items-center justify-center'
      style={{
        backgroundColor: '#3E4857',
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='267' height='267' viewBox='0 0 800 800'%3E%3Cg fill='none' stroke='%232A313C' stroke-width='1.8'%3E%3Cpath d='M769 229L1037 260.9M927 880L731 737 520 660 309 538 40 599 295 764 126.5 879.5 40 599-197 493 102 382-31 229 126.5 79.5-69-63'/%3E%3Cpath d='M-31 229L237 261 390 382 603 493 308.5 537.5 101.5 381.5M370 905L295 764'/%3E%3Cpath d='M520 660L578 842 731 737 840 599 603 493 520 660 295 764 309 538 390 382 539 269 769 229 577.5 41.5 370 105 295 -36 126.5 79.5 237 261 102 382 40 599 -69 737 127 880'/%3E%3Cpath d='M520-140L578.5 42.5 731-63M603 493L539 269 237 261 370 105M902 382L539 269M390 382L102 382'/%3E%3Cpath d='M-222 42L126.5 79.5 370 105 539 269 577.5 41.5 927 80 769 229 902 382 603 493 731 737M295-36L577.5 41.5M578 842L295 764M40-201L127 80M102 382L-261 269'/%3E%3C/g%3E%3Cg fill='%232A313C'%3E%3Ccircle cx='769' cy='229' r='8'/%3E%3Ccircle cx='539' cy='269' r='8'/%3E%3Ccircle cx='603' cy='493' r='8'/%3E%3Ccircle cx='731' cy='737' r='8'/%3E%3Ccircle cx='520' cy='660' r='8'/%3E%3Ccircle cx='309' cy='538' r='8'/%3E%3Ccircle cx='295' cy='764' r='8'/%3E%3Ccircle cx='40' cy='599' r='8'/%3E%3Ccircle cx='102' cy='382' r='8'/%3E%3Ccircle cx='127' cy='80' r='8'/%3E%3Ccircle cx='370' cy='105' r='8'/%3E%3Ccircle cx='578' cy='42' r='8'/%3E%3Ccircle cx='237' cy='261' r='8'/%3E%3Ccircle cx='390' cy='382' r='8'/%3E%3C/g%3E%3C/svg%3E\")",
      }}
    >
      <Card className='w-full max-w-sm px-3 py-6 bg-white/90 backdrop-blur-sm'>
        <CardHeader className='text-center'>
          <div className='flex flex-col items-center justify-center gap-2'>
            <Image
              src={`${publicBucketBaseUrl}/kach-k-insurer.png`}
              alt='Kach'
              className='shadow-lg'
              width={80}
              height={80}
              style={{
                borderRadius: '25%',
              }}
            />
            <p className='font-semibold text-2xl mt-2 tracking-tighter'>
              Kach | Backoffice
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              id='login-form'
              onSubmit={form.handleSubmit(onLogin)}
              className='flex flex-col gap-4'
            >
              <FormField
                control={form.control}
                name='user'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuario</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder='Ingresa tu usuario'
                        type='text'
                        autoComplete='username'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contraseña</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder='Ingresa tu contraseña'
                        type='password'
                        autoComplete='current-password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                form='login-form'
                type='submit'
                className='w-full'
                disabled={isLoading}
                isLoading={isLoading}
                textCenter
              >
                {isLoading ? 'Ingresando...' : 'Ingresar'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
